/**
 * Leaderboard error classification — ported from the `classifyError` helper in
 * `icespace-web/src/services/LeaderboardSnapshot.ts`, generalized to key off
 * `@djgg/core`'s {@link PlatformError} codes instead of raw Firestore error
 * shapes (the DocStorePort adapter is responsible for mapping backend errors
 * onto PlatformError, so this layer never sees a Firestore type).
 *
 * The DISTINCTION matters: a genuinely empty board (`rows.length === 0`,
 * `error === null`) must render differently from a FAILED read (`error !==
 * null`) — the icespace bug this preserves is showing the misleading "be the
 * first to play!" empty-state card when the read actually failed.
 */
import { PlatformError } from '@djgg/core';

/** Why a leaderboard load could not produce trustworthy rows. `null` = success
 *  (which INCLUDES a genuinely-empty board). */
export type LeaderboardError = 'denied' | 'offline' | 'unknown' | null;

/**
 * Map any thrown value to a coarse, user-facing leaderboard error. Recognizes
 * {@link PlatformError} codes first (the sanctioned platform error type), then
 * falls back to string sniffing for adapters that haven't wrapped yet — mirrors
 * the icespace classifier's `permission`/`offline`/`network` matching.
 */
export function classifyLeaderboardError(e: unknown): Exclude<LeaderboardError, null> {
  if (e instanceof PlatformError) {
    switch (e.code) {
      case 'permission':
        return 'denied';
      case 'network':
      case 'unavailable':
        return 'offline';
      default:
        return 'unknown';
    }
  }
  const code = (e as { code?: string })?.code ?? '';
  const msg = String((e as { message?: string })?.message ?? e ?? '').toLowerCase();
  if (code === 'permission-denied' || msg.includes('permission')) return 'denied';
  if (
    code === 'unavailable' ||
    msg.includes('offline') ||
    msg.includes('network') ||
    msg.includes('client is offline')
  ) {
    return 'offline';
  }
  return 'unknown';
}
